import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Users, User, Building2, Target, Hash, Sparkles, Phone, MessageCircle, Mail, Linkedin, Layers, MapPin, Loader2, CheckSquare, Square } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useIcpMasterData, useIcpConfig } from '../../hooks/useIcpData';
import { resolveIcpNames, resolveApolloLocationsForCountry } from '../../services/icpService';
import { QUANTITY_OPTIONS, CAMPAIGNS } from '../../shared/constants';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import { buildLeadRequestJson } from '../../utils/leadRequestJsonBuilder';
import { getCampaignTypeFromName } from '../../utils/campaignUtils';
import { useOutreachReadiness } from '../../hooks/useOutreachReadiness';
import { markFirstOutreachCompleted } from '../../services/readinessService';
import OutreachReadinessBlocker from '../shared/OutreachReadinessBlocker';

const PlaceRequestPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { masterData, loading: masterLoading } = useIcpMasterData();
  const { config: icpConfig, loading: icpLoading } = useIcpConfig(user?.client_id);
  const readiness = useOutreachReadiness(user?.client_id);
  const [readinessRefreshing, setReadinessRefreshing] = useState(false);

  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState({
    country: '',
    industry: '',
    employee_ranges: [] as string[],
    dm_titles: [] as string[],
    campaign: '',
    quantity_needed: '',
    include_similar_titles: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dmTitlesError, setDmTitlesError] = useState('');
  const [limitError, setLimitError] = useState('');
  const [initialized, setInitialized] = useState(false);

  const resolved = useMemo(() => {
    if (!icpConfig || !masterData) return null;
    return resolveIcpNames(icpConfig, masterData);
  }, [icpConfig, masterData]);

  const countryOptions = useMemo(() => {
    if (!icpConfig || !masterData) return [];
    return icpConfig.countries.map(sel => {
      const country = masterData.countries.find(c => c.id === sel.country_id);
      return country ? { value: country.country_name, label: country.country_name, id: sel.country_id } : null;
    }).filter((c): c is { value: string; label: string; id: number } => !!c);
  }, [icpConfig, masterData]);

  const selectedCountryRegions = useMemo(() => {
    if (!formData.country || !icpConfig || !masterData) return { regions: [], regionNames: [] };
    return resolveApolloLocationsForCountry(formData.country, icpConfig, masterData);
  }, [formData.country, icpConfig, masterData]);

  const industryOptions = useMemo(() => {
    if (!resolved) return [];
    return resolved.industries.map(name => ({ value: name, label: name }));
  }, [resolved]);

  useEffect(() => {
    if (!initialized && resolved && icpConfig) {
      setFormData(prev => ({
        ...prev,
        employee_ranges: resolved.company_sizes,
        dm_titles: resolved.titles,
      }));
      setInitialized(true);
    }
  }, [resolved, icpConfig, initialized]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.dm_titles.length > 15) {
      setDmTitlesError('You can select up to 15 titles only');
      return;
    }

    setIsSubmitting(true);
    setLimitError('');

    try {
      const timestamp = new Date().toISOString();
      const userEmail = user?.email || 'Unknown User';
      const clientId = user?.client_id;

      if (!clientId) {
        throw new Error('Client ID not found. Please log in again.');
      }

      const today = new Date().toISOString().split('T')[0];
      const requestedQty = Number(formData.quantity_needed);

      const [settingsResult, todayRequestsResult] = await Promise.all([
        supabase
          .from('user_settings')
          .select('daily_lead_generation_limit')
          .eq('client_id', clientId)
          .maybeSingle(),
        supabase
          .from('leads_requests')
          .select('quantity_requested')
          .eq('client_id', clientId)
          .gte('created_at', `${today}T00:00:00Z`),
      ]);

      const dailyLimit = settingsResult.data?.daily_lead_generation_limit || 200;
      const alreadyRequested = (todayRequestsResult.data || []).reduce(
        (sum: number, r: any) => sum + (Number(r.quantity_requested) || 0), 0
      );
      const totalProjected = alreadyRequested + requestedQty;

      if (totalProjected > dailyLimit) {
        const remaining = Math.max(0, dailyLimit - alreadyRequested);
        setLimitError(
          `Daily lead generation limit reached. You can request up to ${remaining} more leads today (limit: ${dailyLimit}/day).`
        );
        setIsSubmitting(false);
        return;
      }

      let campaignId: string | null = null;
      if (formData.campaign) {
        const campaignType = getCampaignTypeFromName(formData.campaign);
        if (!campaignType) throw new Error('Invalid campaign selected. Please try again.');

        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .select('campaign_id')
          .eq('type', campaignType)
          .eq('client_id', clientId)
          .eq('user_id', userEmail)
          .maybeSingle();

        if (campaignError) throw new Error('Failed to lookup campaign. Please try again.');
        if (!campaignData) throw new Error(`No ${formData.campaign} found for your account. Please create one first.`);
        campaignId = campaignData.campaign_id;
      }

      let allTitles = [...formData.dm_titles];
      let expandedTitleNames: string[] = [];

      if (formData.include_similar_titles && icpConfig) {
        try {
          const selectedTitleIds = icpConfig.title_ids.filter(id => {
            const title = masterData?.titles.find(t => t.id === id);
            return title && formData.dm_titles.includes(title.title_name);
          });
          if (selectedTitleIds.length > 0) {
            const { data: expanded, error: rpcError } = await supabase.rpc('expand_similar_titles', {
              p_title_ids: selectedTitleIds
            });
            if (!rpcError && expanded && Array.isArray(expanded)) {
              expandedTitleNames = expanded.filter((t: string) => !formData.dm_titles.includes(t));
              allTitles = [...formData.dm_titles, ...expandedTitleNames].slice(0, 25);
            }
          }
        } catch (expandError) {
          console.error('[PlaceRequestPage] Title expansion failed, proceeding with user titles only:', expandError);
        }
      }

      const jsonData = buildLeadRequestJson({
        userEmail,
        clientId,
        country: formData.country,
        regions: selectedCountryRegions.regions.length > 0 ? selectedCountryRegions.regions : undefined,
        industry: formData.industry,
        employeeRanges: formData.employee_ranges,
        dmTitles: allTitles,
        quantityNeeded: Number(formData.quantity_needed),
        campaignId: campaignId,
        includeSimilarTitles: formData.include_similar_titles
      });

      const regionDisplayText = selectedCountryRegions.regionNames.length > 0
        ? selectedCountryRegions.regionNames.join(', ')
        : null;

      const newRequest = {
        created_at: timestamp,
        user_id: userEmail,
        client_id: clientId,
        country: formData.country,
        region: regionDisplayText,
        industry: formData.industry,
        employee_ranges: formData.employee_ranges.join(', '),
        dm_titles: allTitles.join(', '),
        quantity_requested: Number(formData.quantity_needed),
        quantity_delivered: 0,
        status: 'in_progress',
        assigned_to_campaign: campaignId ? true : false,
        campaign_id: campaignId,
        include_similar_titles: formData.include_similar_titles,
        json: jsonData
      };

      const { data: insertedData, error: insertError } = await supabase
        .from('leads_requests')
        .insert([newRequest])
        .select('request_id')
        .single();

      if (insertError) throw insertError;

      const requestId = insertedData?.request_id || '';
      const jsonDataWithRequestId = { ...jsonData, request_id: requestId };

      try {
        const { error: taskError } = await supabase
          .from('tasks_queue')
          .insert([{
            client_id: clientId,
            user_id: userEmail,
            agent: 'leadgen',
            task_type: 'dbleadsrequest',
            status: 'pending',
            created_at: new Date().toISOString(),
            run_at: timestamp,
            json: { ...jsonDataWithRequestId, campaign_id: campaignId }
          }]);

        if (taskError) console.error('[PlaceRequestPage] Failed to create task queue entry:', taskError);
      } catch (taskQueueError) {
        console.error('[PlaceRequestPage] Error creating task queue entry:', taskQueueError);
      }

      setShowForm(false);

      if (clientId) {
        markFirstOutreachCompleted(clientId);
      }
    } catch (error) {
      console.error('Failed to submit request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCompanySizeChange = (size: string) => {
    setFormData(prev => ({
      ...prev,
      employee_ranges: prev.employee_ranges.includes(size)
        ? prev.employee_ranges.filter(s => s !== size)
        : [...prev.employee_ranges, size]
    }));
  };

  const handleDmTitlesChange = (title: string) => {
    const newTitles = formData.dm_titles.includes(title)
      ? formData.dm_titles.filter(t => t !== title)
      : [...formData.dm_titles, title];

    if (newTitles.length > 15) {
      setDmTitlesError('You can select up to 15 titles only');
      return;
    }

    setFormData(prev => ({ ...prev, dm_titles: newTitles }));
    setDmTitlesError('');
  };

  const isFormValid = () => {
    return formData.country &&
           formData.industry &&
           formData.employee_ranges.length > 0 &&
           formData.dm_titles.length > 0 &&
           formData.dm_titles.length <= 15 &&
           formData.quantity_needed;
  };

  const handleGoToDashboard = () => {
    navigate('/leads/outbound');
  };

  const getCampaignIcon = (campaign: string) => {
    switch (campaign) {
      case 'Call Campaign': return <Phone size={16} />;
      case 'WhatsApp Campaign': return <MessageCircle size={16} />;
      case 'Email Campaign': return <Mail size={16} />;
      case 'LinkedIn Campaign': return <Linkedin size={16} />;
      case 'Omni-Channel Campaign': return <Layers size={16} />;
      default: return <Target size={16} />;
    }
  };

  if (masterLoading || icpLoading || readiness.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  if (!readiness.ready) {
    return (
      <OutreachReadinessBlocker
        setupItems={readiness.setupItems}
        agentItems={readiness.agentItems}
        connectionItems={readiness.connectionItems}
        completedCount={readiness.completedCount}
        totalCount={readiness.totalCount}
        isFirstTime={readiness.isFirstTime}
        refreshing={readinessRefreshing}
        onRefresh={async () => {
          setReadinessRefreshing(true);
          await readiness.refresh();
          setReadinessRefreshing(false);
        }}
      />
    );
  }

  if (!icpConfig || !resolved || countryOptions.length === 0 || industryOptions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  const availableSizes = resolved.company_sizes;
  const availableTitles = resolved.titles;

  return (
    <div className="max-w-6xl mx-auto">
      {showForm ? (
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-5 border-2 border-[#1875FF]/10 shadow-sm hover:border-[#1875FF]/30 transition-all">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Building2 className="text-[#1875FF]" size={18} />
                    </div>
                    <label htmlFor="country" className="text-base font-bold text-gray-900">
                      Target Country
                    </label>
                  </div>
                  <SimpleCustomSelect
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={(value) => handleSelectChange('country', value)}
                    options={countryOptions}
                    placeholder="Select a country"
                  />
                  {formData.country && selectedCountryRegions.regionNames.length > 0 && (
                    <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-gradient-to-r from-[#05E0D1]/5 to-[#1875FF]/5 rounded-lg border border-[#05E0D1]/20">
                      <MapPin className="text-[#05E0D1] mt-0.5 flex-shrink-0" size={14} />
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Targeting specific regions from your ICP settings</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedCountryRegions.regionNames.map(name => (
                            <span key={name} className="inline-flex items-center px-2.5 py-1 bg-white/80 text-[#05E0D1] rounded-md text-xs font-medium border border-[#05E0D1]/20">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {formData.country && selectedCountryRegions.regionNames.length === 0 && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-[#1875FF]/5 rounded-lg border border-[#1875FF]/10">
                      <MapPin className="text-[#1875FF] flex-shrink-0" size={14} />
                      <p className="text-xs font-medium text-gray-500">Targeting entire country</p>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl p-5 border-2 border-[#1875FF]/10 shadow-sm hover:border-[#1875FF]/30 transition-all">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 rounded-lg flex items-center justify-center">
                      <Target className="text-[#05E0D1]" size={18} />
                    </div>
                    <label htmlFor="industry" className="text-base font-bold text-gray-900">
                      Target Industry
                    </label>
                  </div>
                  <SimpleCustomSelect
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={(value) => handleSelectChange('industry', value)}
                    options={industryOptions}
                    placeholder="Select an industry"
                  />
                </div>

                <div className="bg-white rounded-xl p-5 border-2 border-[#1875FF]/10 shadow-sm hover:border-[#1875FF]/30 transition-all">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Users className="text-[#1875FF]" size={18} />
                    </div>
                    <label className="text-base font-bold text-gray-900">
                      Company Size
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {availableSizes.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleCompanySizeChange(size)}
                        className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all text-center ${
                          formData.employee_ranges.includes(size)
                            ? 'border-[#1875FF] bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-[#1875FF]/30 hover:shadow-sm'
                        }`}
                      >
                        {size} employees
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 border-2 border-[#1875FF]/10 shadow-sm hover:border-[#1875FF]/30 transition-all">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Hash className="text-[#1875FF]" size={18} />
                    </div>
                    <label className="text-base font-bold text-gray-900">
                      Quantity Needed
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {QUANTITY_OPTIONS.map(quantity => (
                      <button
                        key={quantity}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, quantity_needed: quantity.toString() }))}
                        className={`px-3 py-2.5 rounded-lg border-2 font-medium transition-all text-center ${
                          formData.quantity_needed === quantity.toString()
                            ? 'border-[#1875FF] bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-[#1875FF]/30 hover:shadow-sm'
                        }`}
                      >
                        {quantity}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-xl p-5 border-2 border-[#05E0D1]/20 shadow-sm hover:border-[#05E0D1]/40 transition-all">
                  <div className="mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 rounded-lg flex items-center justify-center">
                          <Target className="text-[#05E0D1]" size={18} />
                        </div>
                        <label className="text-base font-bold text-gray-900">
                          Decision-Maker Titles
                        </label>
                      </div>
                      <div className="text-sm text-[#1875FF] font-bold bg-[#1875FF]/10 px-3 py-1 rounded-lg">
                        {formData.dm_titles.length}/15
                      </div>
                    </div>
                    {dmTitlesError && (
                      <p className="text-red-600 text-sm mb-3 mt-2 bg-red-50 p-2 rounded-lg border border-red-200">{dmTitlesError}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {availableTitles.map(title => (
                      <button
                        key={title}
                        type="button"
                        onClick={() => handleDmTitlesChange(title)}
                        className={`px-3 py-3 rounded-lg border-2 font-medium transition-all text-left flex items-center space-x-2 whitespace-nowrap ${
                          formData.dm_titles.includes(title)
                            ? 'border-[#05E0D1] bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 text-[#05E0D1] shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-[#05E0D1]/30 hover:shadow-sm'
                        }`}
                      >
                        <User size={14} className={`flex-shrink-0 ${formData.dm_titles.includes(title) ? 'text-[#05E0D1]' : 'text-gray-400'}`} />
                        <span className="text-[13px]">{title}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, include_similar_titles: !prev.include_similar_titles }))}
                    className="mt-4 flex items-center justify-between gap-3 w-full px-3 py-2.5 rounded-lg border border-gray-200 hover:border-[#05E0D1]/40 transition-all bg-gray-50/50"
                  >
                    <div className="text-left">
                      <span className="text-sm font-medium text-gray-800">Include similar titles in the search</span>
                      <p className="text-xs text-gray-500 mt-0.5">Expands your search by adding related titles at similar seniority levels</p>
                    </div>
                    {formData.include_similar_titles ? (
                      <CheckSquare className="w-5 h-5 text-[#05E0D1] flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                </div>

                <div className="bg-white rounded-xl p-5 border-2 border-[#1875FF]/10 shadow-sm hover:border-[#1875FF]/30 transition-all">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Target className="text-[#1875FF]" size={18} />
                    </div>
                    <label className="text-base font-bold text-gray-900">
                      Assign To Campaign <span className="text-gray-500 font-normal text-xs">(Optional)</span>
                    </label>
                  </div>
                  <div className="flex flex-col gap-2">
                    {CAMPAIGNS.map(campaign => (
                      <button
                        key={campaign}
                        type="button"
                        onClick={() => handleSelectChange('campaign', campaign)}
                        className={`px-3 py-2 rounded-lg border-2 font-medium transition-all text-left flex items-center space-x-2 ${
                          formData.campaign === campaign
                            ? 'border-[#1875FF] bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-[#1875FF]/30 hover:shadow-sm'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          formData.campaign === campaign ? 'bg-[#1875FF]/10' : 'bg-gray-100'
                        }`}>
                          {getCampaignIcon(campaign)}
                        </div>
                        <span className="text-sm">{campaign}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {limitError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm font-medium">
                {limitError}
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid()}
                className="w-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white py-5 px-6 rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-[1.02] focus:ring-4 focus:ring-[#1875FF]/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 flex items-center justify-center space-x-3"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting Request...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Request</span>
                    <ArrowRight size={24} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="text-center py-12 animate-in zoom-in-95 duration-500">
          <div className="mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Sparkles className="text-white" size={48} />
            </div>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight font-display mb-6">Request Submitted Successfully!</h2>
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm max-w-2xl mx-auto">
              <p className="text-lg text-gray-700 leading-relaxed">
                Your lead request has been received and queued for processing.<br />
                <span className="text-[#1875FF] font-bold">AI Lead Gen Agent</span> is now working to generate qualified leads based on your criteria.
              </p>
            </div>
          </div>

          <button
            onClick={handleGoToDashboard}
            className="bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white py-4 px-10 rounded-xl font-bold text-lg hover:shadow-lg hover:scale-[1.02] focus:ring-4 focus:ring-[#1875FF]/30 transition-all duration-300 flex items-center space-x-3 mx-auto"
          >
            <span>Check Qualified Leads Here</span>
            <ArrowRight size={24} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PlaceRequestPage;
